import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  Chip,
  Stack,
  IconButton,
  CardActions,
  Button,
} from '@mui/material';
import {
  Book as BookIcon,
  VideoLibrary as VideoIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';

const resources = [
  {
    id: 1,
    title: 'Logical Reasoning Fundamentals',
    description: 'A comprehensive guide to mastering LSAT logical reasoning basics, including argument structure and common terms.',
    type: 'guide',
    difficulty: 'beginner',
    path: '/guides/lr-fundamentals',
  },
  {
    id: 2,
    title: 'Advanced Flaw Questions',
    description: 'Deep dive into identifying and solving complex flaw questions, with strategies for tackling the most challenging examples.',
    type: 'practice',
    difficulty: 'advanced',
    path: '/practice/flaw-questions',
  },
  {
    id: 3,
    title: 'Assumption Questions Explained',
    description: 'Video tutorial on tackling assumption questions effectively, including necessary vs sufficient assumptions.',
    type: 'video',
    difficulty: 'intermediate',
    path: '/videos/assumption-questions',
  },
  {
    id: 4,
    title: 'Must Be True Questions',
    description: 'Learn how to identify valid conclusions and avoid trap answers in Must Be True questions.',
    type: 'guide',
    difficulty: 'beginner',
    path: '/guides/must-be-true',
  },
  {
    id: 5,
    title: 'Strengthen & Weaken Arguments',
    description: 'Master the techniques for strengthening and weakening arguments, with real LSAT examples.',
    type: 'practice',
    difficulty: 'intermediate',
    path: '/practice/strengthen-weaken',
  },
  {
    id: 6,
    title: 'Parallel Reasoning Mastery',
    description: 'Advanced strategies for identifying and solving parallel reasoning questions quickly and accurately.',
    type: 'video',
    difficulty: 'advanced',
    path: '/videos/parallel-reasoning',
  },
  {
    id: 7,
    title: 'Conditional Logic Deep Dive',
    description: 'Comprehensive guide to understanding and diagramming conditional statements in LR questions.',
    type: 'guide',
    difficulty: 'intermediate',
    path: '/guides/conditional-logic',
  },
  {
    id: 8,
    title: 'Time Management Strategies',
    description: 'Learn effective techniques for managing time during the Logical Reasoning section.',
    type: 'video',
    difficulty: 'beginner',
    path: '/videos/time-management',
  },
  {
    id: 9,
    title: 'Common Logical Fallacies',
    description: 'Interactive practice for identifying and understanding the most common logical fallacies.',
    type: 'practice',
    difficulty: 'beginner',
    path: '/practice/logical-fallacies',
  },
  {
    id: 10,
    title: 'Advanced Principle Questions',
    description: 'Strategies for matching principles to scenarios and identifying underlying principles in arguments.',
    type: 'practice',
    difficulty: 'advanced',
    path: '/practice/principle-questions',
  },
  {
    id: 11,
    title: 'Question Type Identification',
    description: 'Quick techniques for identifying question types and selecting appropriate strategies.',
    type: 'video',
    difficulty: 'beginner',
    path: '/videos/question-types',
  },
  {
    id: 12,
    title: 'Role of Statements',
    description: 'Learn to identify and understand different roles statements play in LR arguments.',
    type: 'guide',
    difficulty: 'intermediate',
    path: '/guides/statement-roles',
  },
  {
    id: 13,
    title: 'Advanced Inference Questions',
    description: 'Master the art of drawing valid inferences and avoiding unwarranted assumptions.',
    type: 'practice',
    difficulty: 'advanced',
    path: '/practice/inference-questions',
  },
  {
    id: 14,
    title: 'Common Answer Traps',
    description: 'Video series on identifying and avoiding common wrong answer choices in LR.',
    type: 'video',
    difficulty: 'intermediate',
    path: '/videos/answer-traps',
  },
  {
    id: 15,
    title: 'Argument Structure Analysis',
    description: 'Learn to break down complex arguments and identify premises, conclusions, and assumptions.',
    type: 'guide',
    difficulty: 'beginner',
    path: '/guides/argument-structure',
  }
];

const LearningResources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [savedResources, setSavedResources] = useState([]);

  const difficulties = ['beginner', 'intermediate', 'advanced'];

  const filterResources = () => {
    return resources.filter(resource => {
      const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDifficulty = !selectedDifficulty || resource.difficulty === selectedDifficulty;
      return matchesSearch && matchesDifficulty;
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case 'guide':
        return <BookIcon />;
      case 'video':
        return <VideoIcon />;
      case 'practice':
        return <AssignmentIcon />;
      default:
        return <BookIcon />;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return '#4CAF50';
      case 'intermediate':
        return '#FF9800';
      case 'advanced':
        return '#F44336';
      default:
        return '#757575';
    }
  };

  const toggleSave = (resourceId) => {
    setSavedResources(prev =>
      prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Learning Resources
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Explore our comprehensive collection of LSAT Logical Reasoning study materials.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1}>
              {difficulties.map((difficulty) => (
                <Chip
                  key={difficulty}
                  label={difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                  onClick={() => setSelectedDifficulty(selectedDifficulty === difficulty ? '' : difficulty)}
                  color={selectedDifficulty === difficulty ? 'primary' : 'default'}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {filterResources().map((resource) => (
          <Grid item xs={12} sm={6} md={4} key={resource.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getIcon(resource.type)}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {resource.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {resource.description}
                </Typography>
                <Chip
                  label={resource.difficulty}
                  size="small"
                  sx={{
                    bgcolor: `${getDifficultyColor(resource.difficulty)}20`,
                    color: getDifficultyColor(resource.difficulty),
                    textTransform: 'capitalize',
                  }}
                />
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: '#4F46E5',
                    '&:hover': {
                      bgcolor: '#3730A3',
                    }
                  }}
                >
                  View Resource
                </Button>
                <IconButton
                  onClick={() => toggleSave(resource.id)}
                  color={savedResources.includes(resource.id) ? 'primary' : 'default'}
                >
                  {savedResources.includes(resource.id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filterResources().length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No resources found matching your criteria.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default LearningResources; 